import { getWmoInfo } from '../utils/wmoWeather';

export interface DayForecast {
  date: string;
  weatherCode: number;
  emoji: string;
  tempMax: number;
  tempMin: number;
  precipitation: number;
}

export interface AttractionWeather {
  current: { temp: number; weatherCode: number; emoji: string };
  forecast: DayForecast[];
}

export interface Attraction {
  id: string;
  nameZH: string;
  nameEN: string;
  descZH: string;
  descEN: string;
  lat: number;
  lng: number;
  imageUrl?: string;
  wikidataUrl: string;
}

// ── Cache ─────────────────────────────────────────────────────────────────────
const cache = new Map<string, { data: any; at: number }>();
const TTL = 30 * 60 * 1000;

// ── Overpass API (OpenStreetMap) attractions by bounding box ──────────────────
const TOURISM_TYPES = 'attraction|museum|gallery|zoo|theme_park|viewpoint|artwork|castle|fort|palace';

/** Synchronous peek — returns cached attractions if available, null otherwise. */
export function peekCachedAttractions(
  north: number, south: number, east: number, west: number,
): Attraction[] | null {
  const key = `attr:${south.toFixed(1)},${west.toFixed(1)},${north.toFixed(1)},${east.toFixed(1)}`;
  const cached = cache.get(key);
  return cached && Date.now() - cached.at < TTL ? cached.data : null;
}

const OVERPASS_ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
  'https://overpass.openstreetmap.ru/api/interpreter',
];

export async function fetchAttractionsByBounds(
  north: number, south: number, east: number, west: number,
): Promise<Attraction[]> {
  const key = `attr:${south.toFixed(1)},${west.toFixed(1)},${north.toFixed(1)},${east.toFixed(1)}`;
  const cached = cache.get(key);
  if (cached && Date.now() - cached.at < TTL) return cached.data;

  const bbox = `${south},${west},${north},${east}`;
  const query = `[out:json][timeout:25];
(
  node["tourism"~"^(${TOURISM_TYPES})$"](${bbox});
  way["tourism"~"^(${TOURISM_TYPES})$"](${bbox});
  relation["tourism"~"^(${TOURISM_TYPES})$"](${bbox});
  node["historic"~"^(castle|monument|ruins|fort|palace|archaeological_site|memorial)$"](${bbox});
  way["historic"~"^(castle|monument|ruins|fort|palace|archaeological_site|memorial)$"](${bbox});
);
out center 100;`;

  const body    = 'data=' + encodeURIComponent(query);
  const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };

  let lastErr: unknown;
  for (const endpoint of OVERPASS_ENDPOINTS) {
    let res: Response;
    try {
      res = await fetch(endpoint, { method: 'POST', body, headers });
    } catch (err) {
      console.warn(`[Overpass] ${endpoint} network error:`, err);
      lastErr = err;
      continue;
    }
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      console.warn(`[Overpass] ${endpoint} HTTP ${res.status}:`, detail);
      lastErr = new Error(`HTTP ${res.status}`);
      continue;
    }

    const json = await res.json();
    const seen = new Set<string>();
    const data: Attraction[] = (json.elements as any[])
      .filter((el: any) => {
        const lat = el.lat ?? el.center?.lat;
        const lon = el.lon ?? el.center?.lon;
        if (!lat || !lon) return false;
        const tags = el.tags || {};
        return !!(tags['name:zh-TW'] || tags['name:zh'] || tags['name:en'] || tags.name);
      })
      .map((el: any) => {
        const lat  = el.lat ?? el.center?.lat;
        const lng  = el.lon ?? el.center?.lon;
        const tags = el.tags || {};

        const nameZH = tags['name:zh-TW'] || tags['name:zh'] || tags.name || '';
        const nameEN = tags['name:en'] || tags.name || '';
        const descZH = tags['description:zh-TW'] || tags['description:zh'] || '';
        const descEN = tags['description:en'] || tags.description || '';

        const wiki = tags.wikipedia || '';
        const wikimediaImg: string | undefined = tags.image || undefined;
        let extUrl = `https://www.openstreetmap.org/${el.type}/${el.id}`;
        if (wiki) {
          const parts = wiki.split(':');
          const lang  = parts.length > 1 ? parts[0] : 'en';
          const title = parts.length > 1 ? parts.slice(1).join(':') : parts[0];
          extUrl = `https://${lang}.wikipedia.org/wiki/${encodeURIComponent(title)}`;
        }

        return { id: String(el.id), nameZH, nameEN, descZH, descEN, lat, lng, imageUrl: wikimediaImg, wikidataUrl: extUrl };
      })
      .filter((a: Attraction) => {
        const dedup = `${a.lat.toFixed(4)},${a.lng.toFixed(4)}`;
        if (seen.has(dedup)) return false;
        seen.add(dedup);
        return true;
      });

    cache.set(key, { data, at: Date.now() });
    return data;
  }

  throw lastErr ?? new Error('All Overpass endpoints failed');
}

// ── Weather for a specific date range (forecast or historical) ────────────────
export async function fetchWeatherForDate(
  lat: number, lng: number, startDate: string, days = 7,
): Promise<DayForecast[]> {
  const endD = new Date(startDate + 'T12:00:00');
  endD.setDate(endD.getDate() + days - 1);
  const endDate = endD.toISOString().slice(0, 10);

  const key = `wxd:${lat.toFixed(3)},${lng.toFixed(3)},${startDate}`;
  const cached = cache.get(key);
  if (cached && Date.now() - cached.at < TTL) return cached.data;

  const today = new Date().toISOString().slice(0, 10);
  const isHistorical = startDate < today;
  const base = isHistorical
    ? 'https://archive-api.open-meteo.com/v1/archive'
    : 'https://api.open-meteo.com/v1/forecast';

  const params = new URLSearchParams({
    latitude:   String(lat),
    longitude:  String(lng),
    daily:      'temperature_2m_max,temperature_2m_min,weather_code,precipitation_sum',
    timezone:   'auto',
    start_date: startDate,
    end_date:   endDate,
  });

  const res = await fetch(`${base}?${params}`);
  if (!res.ok) throw new Error('Weather fetch failed');
  const json = await res.json();

  const forecast: DayForecast[] = (json.daily.time as string[]).map((date, i) => {
    const { emoji } = getWmoInfo(json.daily.weather_code[i]);
    return {
      date,
      weatherCode:   json.daily.weather_code[i],
      emoji,
      tempMax:       Math.round(json.daily.temperature_2m_max[i]),
      tempMin:       Math.round(json.daily.temperature_2m_min[i]),
      precipitation: Math.round(json.daily.precipitation_sum?.[i] ?? 0),
    };
  });

  cache.set(key, { data: forecast, at: Date.now() });
  return forecast;
}

// ── 7-day weather for an attraction (lazy, called when card opens) ─────────────
export async function fetchAttractionWeather(lat: number, lng: number): Promise<AttractionWeather> {
  const key = `wx7:${lat.toFixed(3)},${lng.toFixed(3)}`;
  const cached = cache.get(key);
  if (cached && Date.now() - cached.at < TTL) return cached.data;

  const params = new URLSearchParams({
    latitude:      String(lat),
    longitude:     String(lng),
    current:       'temperature_2m,weather_code',
    daily:         'temperature_2m_max,temperature_2m_min,weather_code,precipitation_sum',
    timezone:      'auto',
    forecast_days: '7',
  });

  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`);
  if (!res.ok) throw new Error('Weather fetch failed');
  const json = await res.json();
  const cur = json.current;
  const { emoji: curEmoji } = getWmoInfo(cur.weather_code);

  const forecast: DayForecast[] = (json.daily.time as string[]).map((date, i) => {
    const { emoji } = getWmoInfo(json.daily.weather_code[i]);
    return {
      date,
      weatherCode:   json.daily.weather_code[i],
      emoji,
      tempMax:       Math.round(json.daily.temperature_2m_max[i]),
      tempMin:       Math.round(json.daily.temperature_2m_min[i]),
      precipitation: Math.round(json.daily.precipitation_sum?.[i] ?? 0),
    };
  });

  const data: AttractionWeather = {
    current: {
      temp:        Math.round(cur.temperature_2m),
      weatherCode: cur.weather_code,
      emoji:       curEmoji,
    },
    forecast,
  };
  cache.set(key, { data, at: Date.now() });
  return data;
}
